import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Facebook, Instagram, MessageCircle, ChevronDown, Utensils, Users, Leaf, Wine, Accessibility } from "lucide-react";
import useLenis from "@/hooks/useLenis";
import Reveal from "@/components/Reveal";
import InquiryForm from "@/components/InquiryForm";
import QuickFacts from "@/components/QuickFacts";
import KindWords from "@/components/KindWords";
import { ScrollProgress, FloatingActions, MobileJumpMenu } from "@/components/Navigation";

const IMG = {
  cityscape:
    "https://customer-assets.emergentagent.com/job_paprikash-tales/artifacts/tfvbac6x_453ACD24-FF85-412E-ACC2-EEF2981D0A8B.jpeg",
  porcelain:
    "https://customer-assets.emergentagent.com/job_paprikash-tales/artifacts/z2h4j752_IMG_1225.jpeg",
  balcony:
    "https://customer-assets.emergentagent.com/job_paprikash-tales/artifacts/0o529ioy_IMG_0481.jpeg",
  kitchen:
    "https://customer-assets.emergentagent.com/job_paprikash-tales/artifacts/ty3t4uoe_IMG_1201.jpeg",
  cutlery:
    "https://customer-assets.emergentagent.com/job_paprikash-tales/artifacts/o4ke7095_IMG_1215.jpeg",
  portrait:
    "https://customer-assets.emergentagent.com/job_paprikash-tales/artifacts/e3evvl8a_IMG_0487.jpeg",
};

const DISHES = [
  {
    n: "I.",
    title: "Clear chicken soup",
    note: "Húsleves",
    body:
      "A broth that asks for patience. Way too many carrots cut on the diagonal, a place where a single parsnip would be happy but it silently needs some partners, a whisper of celery — if by whisper we mean a kilogram.\n\nThe first taste of the afternoon.",
    surprise: "Garlic and crème fraîche",
  },
  {
    n: "II.",
    title: "Boeuf salad",
    note: "Where the beef is just a memory",
    body:
      "Veggies from the freshly boiled soup, potatoes and pickles, confronted by home made mayonnaise.",
    surprise: "Faux mayonnaise",
  },
  {
    n: "III.",
    title: "Chicken paprikash",
    note: "Csirkepaprikás",
    body:
      "Sweet Hungarian paprika blooming in fat. Onions cooked the slow way. A sauce so rich you'll want to slide your bread across the plate twice.",
    surprise: "Curcuma",
  },
];

const INCLUDED = [
  "Three traditional dishes and their modern counterparts",
  "Homemade fruit & powdered-milk ice cream (or cocoa-banana)",
  "A selection of traditional Transylvanian plum brandy (pălincă)",
  "Homemade lemonade — with or without sugar or honey",
  "Plain and sparkling water at the table",
];

const DETAILS = [
  { k: "When", v: "We begin at 12-ish" },
  { k: "Length", v: "Four to five hours" },
  { k: "Where", v: "Andrea's Home, Uliului street, Cluj-Napoca" },
  { k: "Group", v: "An intimate 2 to 8 guests" },
  { k: "Kitchen", v: "Nut-free for us — but if you'd like to munch on some, feel free to bring them along" },
  { k: "Not for", v: "Vegans and vegetarians" },
];

const EXPECT = [
  {
    icon: Utensils,
    title: "Food & Folklore",
    body:
      "You'll hear life-changing stories through generations of a Transylvanian family that shaped the recipes of our gathering — and you'll be warmly invited to share your own, if you'd like, all while we cook together.",
  },
  {
    icon: Users,
    title: "Intimate Setting",
    body:
      "This is a private experience for a small group of 2 – 8 people. Every gathering is hand-arranged in my own home.",
  },
  {
    icon: Leaf,
    title: "Dietary Notes",
    body:
      "My kitchen is strictly nut-free and my adapted recipes are curated to be allergen-free for most major categories. Because this menu is built entirely around chicken, it is not suitable for vegans or vegetarians. Please let me know in advance if you have any allergies so that I can prepare accordingly.",
  },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <img src={IMG.cityscape} alt="Cluj-Napoca skies" className="photo ken" />
        {/* Layered darkening: deep at top for nav, soft vignette at left for headline legibility, gentle fade to paper at bottom */}
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(20,18,16,0.6)] via-[rgba(20,18,16,0.3)] to-[rgba(249,246,240,0.85)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(20,18,16,0.7)] via-[rgba(20,18,16,0.35)] to-transparent sm:to-transparent" />
      </motion.div>

      {/* Top bar */}
      <div className="relative z-10 px-6 sm:px-10 md:px-16 lg:px-24 pt-8 sm:pt-10 flex items-center justify-between">
        <p className="font-display italic text-[color:var(--paper)] text-lg tracking-wide [text-shadow:0_2px_8px_rgba(0,0,0,0.6)]">
          Andrea<span className="text-[color:var(--gold)]"> · </span>Cluj
        </p>
        <nav className="hidden sm:flex items-center gap-8 text-[0.78rem] uppercase tracking-[0.22em] text-[color:var(--paper)] [text-shadow:0_2px_8px_rgba(0,0,0,0.7)]">
          <a href="#story" className="hover:text-[color:var(--gold)] transition-colors">The Story</a>
          <a href="#menu" className="hover:text-[color:var(--gold)] transition-colors">The Menu</a>
          <a href="#details" className="hover:text-[color:var(--gold)] transition-colors">Details</a>
          <a href="#inquire" className="hover:text-[color:var(--gold)] transition-colors">Inquire</a>
        </nav>
      </div>

      {/* Title block */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 px-6 sm:px-10 md:px-16 lg:px-24 pt-[18svh] sm:pt-[22svh] pb-24 max-w-6xl"
      >
        <p className="eyebrow text-[color:var(--paper)] [text-shadow:0_2px_14px_rgba(0,0,0,0.9)] inline-block">
          A Transylvanian story · Cluj-Napoca
        </p>
        <h1
          className="font-display text-[color:var(--paper)] mt-6 text-[clamp(3rem,9vw,8rem)] leading-[0.88] tracking-tight [text-shadow:0_4px_24px_rgba(0,0,0,0.55)]"
        >
          The Unfinished<br />
          <em className="italic font-display text-[color:var(--gold)] [text-shadow:0_4px_24px_rgba(0,0,0,0.55)]">Lunch.</em>
        </h1>
        <p className="font-display text-[color:var(--paper)] mt-8 text-[clamp(1.75rem,3.5vw,2.75rem)] leading-tight tracking-tight [text-shadow:0_2px_14px_rgba(0,0,0,0.65)]">
          Cook.<span className="text-[color:var(--gold)]"> </span>
          <em className="italic">Eat.</em><span className="text-[color:var(--gold)]"> </span>
          Listen.
        </p>
        <p className="font-body text-[color:var(--paper)] text-lg sm:text-xl max-w-2xl mt-6 leading-relaxed [text-shadow:0_2px_12px_rgba(0,0,0,0.65)]">
          Step into a private home for some slow living where food, stories
          and good company blend during{" "}
          <a
            href="#estebed"
            className="italic underline decoration-[color:var(--gold)] decoration-2 underline-offset-[6px] hover:text-[color:var(--gold)] transition-colors"
            data-testid="estebed-anchor-link"
          >
            estebéd
          </a>
          .
        </p>

        <div className="mt-12 flex flex-wrap items-center gap-5">
          <a href="#inquire" className="btn-primary" data-testid="hero-cta-button">
            Request your seats at our table
          </a>
          <a
            href="https://wa.me/40746264632"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[color:var(--paper)] text-sm tracking-[0.18em] uppercase hover:text-[color:var(--gold)] transition-colors [text-shadow:0_2px_8px_rgba(0,0,0,0.6)]"
            data-testid="hero-whatsapp-link"
          >
            <MessageCircle size={16} strokeWidth={1.5} /> WhatsApp
          </a>
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-6 sm:left-10 md:left-16 lg:left-24 z-10 flex items-center gap-3 text-[color:var(--paper)] [text-shadow:0_2px_8px_rgba(0,0,0,0.6)]"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <ChevronDown size={18} strokeWidth={1.5} />
        <span className="font-display italic text-sm">scroll, unhurried</span>
      </motion.div>
    </section>
  );
}

function AboutAndrea() {
  return (
    <section id="story" className="relative px-6 sm:px-10 md:px-16 lg:px-24 py-24 sm:py-32" data-testid="about-section">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <Reveal className="lg:col-span-7 lg:col-start-1">
          <p className="eyebrow">
            <span className="text-[color:var(--gold)]">01 ·</span> Your host
          </p>
          <span className="rule" />
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[0.92] tracking-tight">
            Hello, I'm <em className="italic text-[color:var(--moss)]">Andrea.</em>
          </h2>
          <p className="font-display text-xl sm:text-2xl leading-snug text-[color:var(--ink-2)] mt-6 max-w-2xl">
            — a musician deeply rooted in Transylvanian culture, with a
            six-hundred-year-old family tree grounded here and the stories to
            go with that.
          </p>

          <div className="mt-8 flex items-center gap-5">
            <img
              src={IMG.portrait}
              alt="Andrea — your host"
              className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover photo-shadow ring-1 ring-[color:var(--rule)]"
              data-testid="andrea-portrait"
            />
            <p className="font-hand text-2xl text-[color:var(--moss)] leading-snug max-w-[14rem]">
              That's me somewhere<br />between blues and stew.
            </p>
          </div>

          <div className="mt-10 max-w-prose space-y-6 font-body text-[1.06rem] leading-[1.85] text-[color:var(--ink-2)]">
            <p className="drop-cap">
              <strong className="italic font-semibold text-[color:var(--ink)]">"How do you feed a whole family from a few chicken thighs?"</strong>{" "}
              <strong className="font-semibold text-[color:var(--ink)]">In times of scarcity, this was a daily puzzle that sparked amazing culinary creativity.</strong>
            </p>
            <p>
              Spend an afternoon in my kitchen and we won't just cook — we'll
              explore how a few simple ingredients become three traditional
              dishes, family stories and a conversation between the past and
              the present.
            </p>
            <p>
              This is not a cooking class. It is not a restaurant. It is an
              invitation to my home — a living, breathing project where you'll
              notice the raw character of a new house still being finished and
              also the warmth of a place where <em>food equals care</em>.
            </p>
            <p className="font-hand text-2xl text-[color:var(--moss)] leading-snug pt-2">
              — Andrea
            </p>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-5 lg:col-start-8 lg:-mt-6" delay={0.15}>
          <div className="relative">
            <img
              src={IMG.porcelain}
              alt="Vintage heirloom porcelain in dark wood cabinet"
              className="photo photo-shadow aspect-[4/5] object-cover"
            />
            <p className="font-hand text-xl text-[color:var(--ink-2)] mt-4 italic">
              the dishes my grandmother set the table with
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Estebed() {
  return (
    <section id="estebed" className="relative py-28 sm:py-40 overflow-hidden" data-testid="estebed-section">
      <div className="px-6 sm:px-10 md:px-16 lg:px-24 max-w-5xl mx-auto text-center">
        <Reveal>
          <p className="eyebrow">
            <span className="text-[color:var(--gold)]">02 ·</span> The word you have been looking for when skipping lunch
          </p>
          <h2 className="font-display italic text-[clamp(4rem,12vw,9rem)] leading-[0.9] text-[color:var(--moss)] mt-8 tracking-tight">
            estebéd
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="font-body text-xl sm:text-2xl leading-relaxed mt-10 max-w-2xl mx-auto text-[color:var(--ink-2)]">
            <span className="text-[color:var(--ink)]">noun, Transylvanian Hungarian.</span>{" "}
            The unhurried meal that happens somewhere between lunch and dinner —
            when hunger decides the hour, not the clock.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function DishItem({ dish, index }) {
  return (
    <li className="grid grid-cols-[auto_1fr] gap-8 border-t border-[color:var(--rule)] pt-10">
      <span className="font-display italic text-5xl text-[color:var(--gold)] leading-none">
        {dish.n}
      </span>
      <div>
        <h3 className="font-display text-3xl sm:text-4xl text-[color:var(--ink)] tracking-tight">
          {dish.title}
        </h3>
        <p className="font-hand text-xl text-[color:var(--moss)] mt-1">{dish.note}</p>
        <div className="font-body text-[1.02rem] leading-relaxed text-[color:var(--ink-2)] mt-4 max-w-prose space-y-3">
          {dish.body.split("\n\n").map((para) => (
            <p key={para}>{para}</p>
          ))}
        </div>
        <p
          className="mt-5 font-body text-sm text-[color:var(--ink-2)]"
          data-testid={`dish-surprise-${index}`}
        >
          <span className="font-hand text-lg text-[color:var(--gold)] mr-2">
            mystery ingredient twist
          </span>
          <span className="font-display italic text-[color:var(--moss)] text-lg">
            {dish.surprise}
          </span>
        </p>
      </div>
    </li>
  );
}

function Menu() {
  return (
    <section id="menu" className="relative px-6 sm:px-10 md:px-16 lg:px-24 py-24 sm:py-32" data-testid="menu-section">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow">
            <span className="text-[color:var(--gold)]">03 ·</span> What we'll do
          </p>
          <span className="rule" />
          <h2 className="font-display text-4xl sm:text-5xl leading-[0.95] tracking-tight">
            Three dishes —<br />
            <em className="italic text-[color:var(--moss)]">and their modern echoes.</em>
          </h2>
          <div className="mt-8 space-y-5 font-body text-[1.05rem] leading-relaxed text-[color:var(--ink-2)] max-w-md">
            <p>
              We will prepare three dishes exactly as my family has for
              generations: clear chicken soup, a traditional "boeuf" salad — in
              which beef evolved to be just a memory — and a rich chicken
              paprikash.
            </p>
            <p>
              Then, we'll do something my grandmother might have found mildly
              offensive: we will reinvent them. We'll taste our traditional
              versions side-by-side with modern, healthier or richer
              interpretations. Not molecular cuisine, just a slightly different
              point of view.
            </p>
          </div>

          <div className="mt-10">
            <img
              src={IMG.kitchen}
              alt="Andrea's kitchen in Cluj-Napoca"
              className="photo photo-shadow aspect-[4/3]"
            />
            <p className="font-hand text-xl text-[color:var(--ink-2)] italic mt-4">
              Welcome to the kitchen. I live here now.
            </p>
          </div>
        </Reveal>

        <div className="lg:col-span-7 lg:pt-10">
          <ol className="space-y-12">
            {DISHES.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.08}>
                <DishItem dish={d} index={i} />
              </Reveal>
            ))}
          </ol>
          <Reveal delay={0.3}>
            <p
              className="mt-12 pt-10 border-t border-[color:var(--rule)] font-hand text-3xl sm:text-4xl text-[color:var(--moss)] leading-tight"
              data-testid="menu-mystery"
            >
              Learn all the mystery ingredients that evolved these{" "}
              <span className="text-[color:var(--gold)]">amazing recipes.</span>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhatToExpect() {
  return (
    <section className="relative px-6 sm:px-10 md:px-16 lg:px-24 py-24 sm:py-32" data-testid="expect-section">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <Reveal className="lg:col-span-5">
          <div className="relative">
            <img
              src={IMG.balcony}
              alt="Balcony view of Cluj-Napoca from Andrea's home"
              className="photo photo-shadow aspect-[4/5]"
            />
            <p className="font-hand text-xl text-[color:var(--ink-2)] italic mt-4">
              This is where you will be dining. The shoes go, but the concrete
              stays for now.
            </p>
          </div>
        </Reveal>

        <div className="lg:col-span-7 lg:pt-6">
          <Reveal>
            <p className="eyebrow">
              <span className="text-[color:var(--gold)]">04 ·</span> What to expect
            </p>
            <span className="rule" />
            <h2 className="font-display text-4xl sm:text-5xl leading-[0.95] tracking-tight">
              An afternoon shaped by <em className="italic text-[color:var(--moss)]">stories, slowness</em> and the smell of paprika.
            </h2>
          </Reveal>

          <div className="mt-12 space-y-10">
            {EXPECT.map((e, i) => {
              const Icon = e.icon;
              return (
                <Reveal key={e.title} delay={i * 0.08}>
                  <div className="grid grid-cols-[auto_1fr] gap-6 border-t border-[color:var(--rule)] pt-8">
                    <Icon size={22} strokeWidth={1.4} className="text-[color:var(--gold)] mt-1" />
                    <div>
                      <h3 className="font-display text-2xl sm:text-3xl text-[color:var(--ink)] tracking-tight">
                        {e.title}
                      </h3>
                      <p className="font-body text-[1.02rem] leading-relaxed text-[color:var(--ink-2)] mt-3 max-w-prose">
                        {e.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function Callout({ icon: Icon, title, children, tone = "neutral", testId }) {
  const tones = {
    neutral: {
      box: "border border-[color:var(--rule)] bg-[color:var(--paper)]",
      icon: "text-[color:var(--gold)]",
    },
    moss: {
      box: "border border-[color:var(--moss)]/30 bg-[color:var(--moss)]/[0.06]",
      icon: "text-[color:var(--moss)]",
    },
  };
  const t = tones[tone];
  return (
    <div className={`flex items-start gap-4 p-6 ${t.box}`} data-testid={testId}>
      <Icon size={22} strokeWidth={1.4} className={`${t.icon} mt-1 flex-none`} />
      <div>
        <p className="font-hand text-xl text-[color:var(--moss)]">{title}</p>
        <p className="font-body text-[1.02rem] leading-relaxed text-[color:var(--ink-2)] mt-1">
          {children}
        </p>
      </div>
    </div>
  );
}

function Details() {
  return (
    <section id="details" className="relative px-6 sm:px-10 md:px-16 lg:px-24 py-24 sm:py-32 bg-[color:var(--paper-2)]/40" data-testid="details-section">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <Reveal className="lg:col-span-5">
          <img
            src={IMG.cutlery}
            alt="Heirloom gold-accented German cutlery in vintage case"
            className="photo photo-shadow aspect-[4/5]"
          />
          <p className="font-hand text-xl text-[color:var(--ink-2)] italic mt-4">
            the silverware still kept in its travelling case
          </p>
        </Reveal>

        <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow">
              <span className="text-[color:var(--gold)]">05 ·</span> What I provide
            </p>
            <span className="rule" />
            <ul className="mt-2 divide-y divide-[color:var(--rule)]">
              {INCLUDED.map((line) => (
                <li
                  key={line}
                  className="py-4 font-body text-[1.05rem] text-[color:var(--ink)] leading-relaxed flex gap-4"
                >
                  <span className="text-[color:var(--gold)] font-display text-xl leading-none mt-1">·</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1} className="mt-14">
            <Callout icon={Wine} title="What to bring">
              If you have a favourite wine you'd like to pair with our
              estebéd, please feel free to bring it. We'll have the glassware
              ready.
            </Callout>
          </Reveal>

          <Reveal delay={0.12} className="mt-4">
            <Callout
              icon={Accessibility}
              title="Accessibility"
              tone="moss"
              testId="accessibility-note"
            >
              Our home is located two levels below street level and is currently
              not wheelchair accessible. We are happy to assist guests with
              mobility needs —{" "}
              <a
                href="https://wa.me/40746264632"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[color:var(--moss)] underline decoration-[color:var(--gold)] decoration-2 underline-offset-[4px] hover:text-[color:var(--moss-dark)] transition-colors"
                data-testid="accessibility-whatsapp-link"
              >
                please reach out before booking
              </a>{" "}
              so we can discuss how to make your visit as comfortable as
              possible.
            </Callout>
          </Reveal>

          <Reveal delay={0.15} className="mt-16">
            <p className="eyebrow">Practical details</p>
            <span className="rule" />
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 mt-4">
              {DETAILS.map((d) => (
                <div key={d.k}>
                  <dt className="font-hand text-xl text-[color:var(--moss)]">{d.k}</dt>
                  <dd className="font-display text-2xl text-[color:var(--ink)] leading-tight mt-1">{d.v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>

          <Reveal delay={0.2} className="mt-16">
            <p className="eyebrow">
              Contribution<span className="text-[color:var(--gold)]">*</span>
            </p>
            <span className="rule" />
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="border border-[color:var(--rule)] p-8 bg-[color:var(--paper)]">
                <p className="font-hand text-xl text-[color:var(--moss)]">For 2 – 4 guests</p>
                <p className="font-display text-5xl text-[color:var(--ink)] mt-2">€85<span className="text-xl text-[color:var(--ink-3)]"> / person</span></p>
              </div>
              <div className="border border-[color:var(--rule)] p-8 bg-[color:var(--paper)]">
                <p className="font-hand text-xl text-[color:var(--moss)]">For 5 – 8 guests</p>
                <p className="font-display text-5xl text-[color:var(--ink)] mt-2">€75<span className="text-xl text-[color:var(--ink-3)]"> / person</span></p>
              </div>
            </div>
            <p className="font-body text-sm italic text-[color:var(--ink-3)] mt-4">
              *prices may vary according to demand
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Closing() {
  return (
    <section className="relative px-6 sm:px-10 md:px-16 lg:px-24 py-32 sm:py-48 overflow-hidden" data-testid="closing-section">
      <div className="max-w-6xl mx-auto text-center">
        <Reveal>
          <p className="eyebrow text-[color:var(--ink-2)]">until then —</p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="font-display text-[clamp(2.6rem,8.5vw,7.5rem)] leading-[0.95] tracking-tight text-[color:var(--ink)] mt-8">
            The only thing<br />
            missing<br />
            <em className="italic text-[color:var(--moss)]">is you.</em>
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-5">
            <a href="#inquire" className="btn-primary" data-testid="closing-cta-button">
              Request your seats at our table
            </a>
            <a
              href="https://wa.me/40746264632"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
              data-testid="closing-whatsapp-link"
            >
              WhatsApp Andrea
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="relative px-6 sm:px-10 md:px-16 lg:px-24 py-16 border-t border-[color:var(--rule)] bg-[color:var(--paper-2)]/60" data-testid="footer">
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-10 items-start">
        <div>
          <p className="font-display text-2xl">theunfinishedlunch</p>
          <p className="font-hand text-xl text-[color:var(--moss)] mt-1">by Andrea, Cluj-Napoca</p>
        </div>
        <div>
          <p className="eyebrow">Find us</p>
          <div className="flex items-center gap-5 mt-4">
            <a
              href="https://www.facebook.com/profile.php?id=61590916353820"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Andrea on Facebook"
              className="text-[color:var(--ink-2)] hover:text-[color:var(--moss)] transition-colors"
              data-testid="social-facebook-link"
            >
              <Facebook size={20} strokeWidth={1.4} />
            </a>
            <a
              href="https://www.instagram.com/theunfinishedlunch/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="@theunfinishedlunch on Instagram"
              className="text-[color:var(--ink-2)] hover:text-[color:var(--moss)] transition-colors"
              data-testid="social-instagram-link"
            >
              <Instagram size={20} strokeWidth={1.4} />
            </a>
            <a
              href="https://wa.me/40746264632"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp Andrea"
              className="text-[color:var(--ink-2)] hover:text-[color:var(--moss)] transition-colors"
              data-testid="footer-whatsapp-link"
            >
              <MessageCircle size={20} strokeWidth={1.4} />
            </a>
          </div>
        </div>
        <div className="font-body text-sm text-[color:var(--ink-2)] leading-relaxed">
          <p>Each experience is hosted personally in my home and scheduled individually with guests.</p>
        </div>
      </div>
      <p className="text-center text-xs uppercase tracking-[0.22em] text-[color:var(--ink-3)] mt-12">
        © {new Date().getFullYear()} · Andrea · Cluj-Napoca, Transylvania
      </p>
    </footer>
  );
}

export default function Home() {
  useLenis();

  return (
    <main className="paper-grain min-h-screen" data-testid="home-page">
      <ScrollProgress />
      <MobileJumpMenu />
      <Hero />
      <AboutAndrea />
      <Estebed />
      <Menu />
      <WhatToExpect />
      <Details />
      <Closing />
      <KindWords />
      <QuickFacts />
      <InquiryForm />
      <Footer />
      <FloatingActions />
    </main>
  );
}
